import React from 'react';
import { Paper, Typography, Box, useTheme, LinearProgress, Button } from '@mui/material';
import { TimerOff, Smartphone, Refresh, Lock, Warning } from '@mui/icons-material';

const TestingTimeStatus = ({ data, refetch }) => {
  const theme = useTheme();

  const deepBlue = {
    light: '#1976d2',
    dark: '#1565c0',
    subtle: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d47a1'
  };

  const {
    current_month_mobile_usage = 0,
    mobile_minutes_allowed = 0,
    feature_enabled = false,
    minutes_available = 0
  } = data || {};

  const isSessionActive = feature_enabled;
  const hasMinutesAvailable = minutes_available > 0;
  const isOverLimit = minutes_available < 0;
  const usagePercentage = Math.min(
    (current_month_mobile_usage / mobile_minutes_allowed) * 100,
    100
  );

  const getComponentState = () => {
    if (!isSessionActive) {
      return 'no-session';
    } else if (isOverLimit) {
      return 'over-limit';
    } else if (!hasMinutesAvailable) {
      return 'no-minutes';
    }
    return 'has-minutes';
  };

  const componentState = getComponentState();

  const stateConfig = {
    'no-session': {
      title: 'Mobile Testing Access Unavailable',
      icon: <Lock sx={{ fontSize: 28, color: theme.palette.error.main }} />,
      description:
        'Your company does not have any mobile testing minutes available.',
      showProgress: false,
      showUsage: false,
      color: theme.palette.error.main
    },
    'over-limit': {
      title: 'Mobile Testing Allocation Exceeded',
      icon: <Warning sx={{ fontSize: 28, color: theme.palette.error.main }} />,
      description:
        "Your company has exceeded the monthly mobile testing allocation. You can't continue testing for now, your mobile testing minutes allocation will reset at the end of the month.",
      showProgress: true,
      showUsage: true,
      color: theme.palette.error.main
    },
    'no-minutes': {
      title: 'You have exhausted the monthly minutes allocation',
      icon: <TimerOff sx={{ fontSize: 28, color: theme.palette.info.main }} />,
      description: 'Your mobile testing minutes allocation will reset at the end of the month',
      showProgress: true,
      showUsage: true,
      color: theme.palette.info.main
    },
    'has-minutes': {
      title: 'Company Mobile Testing Allocation',
      icon: <Smartphone sx={{ fontSize: 28, color: deepBlue.light }} />,
      description:
        "Current status of your company's mobile testing time allocation. This shared resource is available for all team members' testing needs.",
      showProgress: true,
      showUsage: true,
      color: deepBlue.light
    }
  };

  const config = stateConfig[componentState];

  return (
    <Paper
      elevation={1}
      sx={{
        p: 4,
        textAlign: 'center',
        background: `linear-gradient(145deg, ${deepBlue.subtle}10, ${theme.palette.background.paper})`,
        border: `1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#333'}`,
        borderRadius: 2,
        maxWidth: 500,
        mx: 'auto',
        my: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${deepBlue.light}08 0%, transparent 70%)`,
          zIndex: 0
        }}
      />

      <Box
        sx={{
          position: 'relative',
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <Smartphone
            sx={{
              fontSize: 60,
              color: componentState === 'no-session' ? theme.palette.text.disabled : deepBlue.light,
              opacity: componentState === 'no-session' ? 0.5 : 0.9
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -8,
              right: -8,
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50%',
              padding: 0.5,
              boxShadow: 1
            }}>
            {config.icon}
          </Box>
        </Box>
      </Box>

      <Typography
        variant="h6"
        component="h2"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: config.color,
          mb: 2,
          position: 'relative',
          zIndex: 1
        }}>
        {config.title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 3,
          lineHeight: 1.6,
          fontWeight: 500
        }}>
        {config.description}
      </Typography>

      {config.showUsage && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: 700,
              color: config.color,
              mb: 1
            }}>
            {isOverLimit ? `-${Math.abs(minutes_available)}` : minutes_available}
            <Typography variant="body1" component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
              / {mobile_minutes_allowed} minutes
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
            {isOverLimit
              ? `Overage: ${Math.abs(minutes_available)} minutes beyond allocation`
              : hasMinutesAvailable
              ? `Remaining in company mobile testing allocation`
              : 'No mobile testing minutes available in company allocation'}
          </Typography>
        </Box>
      )}

      {config.showProgress && (
        <Box sx={{ mb: 4 }}>
          <LinearProgress
            variant="determinate"
            value={usagePercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#333',
              '& .MuiLinearProgress-bar': {
                backgroundColor: isOverLimit ? theme.palette.error.main : config.color,
                borderRadius: 4
              }
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1,
              mb: 2
            }}>
            <Typography variant="caption" color="text.secondary">
              Monthly usage: {current_month_mobile_usage} minutes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round(usagePercentage)}% utilized
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontStyle: 'italic',
              opacity: 0.8,
              lineHeight: 1.5
            }}>
            {isOverLimit
              ? `Company has used ${current_month_mobile_usage} minutes (${Math.abs(
                  minutes_available
                )} minutes over allocation).`
              : `Company has used ${current_month_mobile_usage} of ${mobile_minutes_allowed} allocated minutes.`}
          </Typography>
        </Box>
      )}

      {
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              lineHeight: 1.5
            }}>
            If you would like to discuss options for increase allocation,{' '}
            <Typography
              as={'a'}
              color="primary"
              href="https://getscandium.com/contact/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 500 }}>
              contact us
            </Typography>
          </Typography>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refetch}
            sx={{
              borderColor: config.color,
              color: config.color,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: config.color,
                backgroundColor: `${config.color}08`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}>
            Refresh Status
          </Button>
        </Box>
      }
    </Paper>
  );
};

export default TestingTimeStatus;
